import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLaptopCode, faPalette, faMobileAlt, faSearchDollar, faDatabase, faCloud } from '@fortawesome/free-solid-svg-icons';

const ServicesSection = styled.section`
  padding: 100px 0;
  background-color: #f9f9f9;
  
  @media (max-width: 768px) {
    padding: 70px 0;
  }
  
  @media (max-width: 576px) {
    padding: 50px 0;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  
  @media (max-width: 576px) {
    padding: 0 15px;
  }
`;

const SectionTitle = styled.div`
  text-align: center;
  margin-bottom: 60px;
  
  @media (max-width: 768px) {
    margin-bottom: 40px;
  }
`;

const SubTitle = styled(motion.span)`
  display: inline-block;
  font-size: 16px;
  font-weight: 500;
  color: #ff6e6e;
  text-transform: uppercase;
  letter-spacing: 2px;
  margin-bottom: 15px;
  position: relative;
  
  &:before, &:after {
    content: '';
    position: absolute;
    top: 50%;
    width: 30px;
    height: 1px;
    background-color: #ff6e6e;
  }
  
  &:before {
    left: -40px;
  }
  
  &:after {
    right: -40px;
  }
  
  @media (max-width: 576px) {
    font-size: 14px;
    letter-spacing: 1px;
    
    &:before, &:after {
      width: 20px;
    }
    
    &:before {
      left: -25px;
    }
    
    &:after {
      right: -25px;
    }
  }
`;

const Title = styled(motion.h2)`
  font-size: 42px;
  font-weight: 700;
  color: #333;
  margin-bottom: 20px;
  
  span {
    color: #ff6e6e;
  }
  
  @media (max-width: 768px) {
    font-size: 32px;
  }
  
  @media (max-width: 576px) {
    font-size: 28px;
  }
`;

const Description = styled(motion.p)`
  font-size: 16px;
  color: #666;
  max-width: 700px;
  margin: 0 auto;
  line-height: 1.6;
  
  @media (max-width: 576px) {
    font-size: 15px;
    max-width: 100%;
  }
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 30px;
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  }
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  }
  
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const ServiceCard = styled(motion.div)`
  background-color: #fff;
  border-radius: 8px;
  padding: 40px 30px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  }
  
  @media (max-width: 576px) {
    padding: 30px 20px;
  }
`;

const IconBox = styled.div`
  width: 70px;
  height: 70px;
  background-color: rgba(255, 94, 20, 0.1);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 25px;
  color: #ff6e6e;
  font-size: 28px;
  transition: all 0.3s ease;
  
  ${ServiceCard}:hover & {
    background-color: #ff6e6e;
    color: #fff;
  }
  
  @media (max-width: 576px) {
    width: 60px;
    height: 60px;
    font-size: 24px;
  }
`;

const ServiceTitle = styled.h3`
  font-size: 22px;
  font-weight: 600;
  color: #333;
  margin-bottom: 15px;
  
  @media (max-width: 576px) {
    font-size: 20px;
  }
`;

const ServiceDescription = styled.p`
  font-size: 15px;
  color: #666;
  line-height: 1.6;
  margin-bottom: 20px;
  
  @media (max-width: 576px) {
    font-size: 14px;
  }
`;

const ReadMore = styled.a`
  font-size: 14px;
  font-weight: 500;
  color: #ff6e6e;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  
  &:after {
    content: '→';
    margin-left: 5px;
    transition: transform 0.3s ease;
  }
  
  &:hover:after {
    transform: translateX(5px);
  }
  
  @media (max-width: 576px) {
    font-size: 13px;
  }
`;

const serviceItems = [
  {
    href: '/services/website-development',
    icon: faLaptopCode,
    title: 'Website Development',
    description: 'We create responsive, high-performance websites and web applications tailored to your business needs.'
  },
  {
    href: '/services/ui-ux-design',
    icon: faPalette,
    title: 'UI/UX Design',
    description: 'Our design team crafts beautiful, intuitive interfaces that enhance user experience and engagement.'
  },
  {
    href: '/services/server-setup',
    icon: faCloud,
    title: 'Server Setup',
    description: 'We set up secure, scalable, and optimized servers tailored to your business—on-premise or in the cloud.'
  },
  {
    href: '/services/mobile-app-development',
    icon: faMobileAlt,
    title: 'Mobile App Development',
    description: 'We build native and cross-platform mobile applications that deliver exceptional user experiences.'
  },
  {
    href: '/services/seo-optimization',
    icon: faSearchDollar,
    title: 'SEO Optimization',
    description: 'Improve your search engine rankings and drive more organic traffic to your website with our SEO expertise.'
  },
  {
    href: '/services/database-management',
    icon: faDatabase,
    title: 'Database Management',
    description: 'We design, implement, and optimize database solutions that ensure data integrity, security, and performance for your business applications.'
  }
];

const cardVariants = {
  hidden: { opacity: 0, y: 50 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: 0.1 * i,
      duration: 0.5,
    }
  })
};

const Services = () => {
  return (
    <ServicesSection id="services">
      <Container>
        <SectionTitle>
          <SubTitle
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            Our Services
          </SubTitle>
          <Title
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            What <span>We Offer</span>
          </Title>
          <Description
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            We offer a comprehensive range of software solutions designed to help businesses thrive in the digital world. Our expert team delivers high-quality, customized software to meet your unique needs and drive success.
          </Description>
        </SectionTitle>
        
        <ServicesGrid>
          {serviceItems.map((service, index) => (
            <ServiceCard
              key={index}
              custom={index}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={cardVariants}
            >
              <IconBox>
                <FontAwesomeIcon icon={service.icon} />
              </IconBox>
              <ServiceTitle>{service.title}</ServiceTitle>
              <ServiceDescription>{service.description}</ServiceDescription>
              <ReadMore href={service.href}>Learn More</ReadMore>
            </ServiceCard>
          ))}
        </ServicesGrid>
      </Container>
    </ServicesSection>
  );
};

export default Services;